import { TELEMETRY_DEFAULT_APP, TELEMETRY_EVENT_TYPES } from "./constants";
import {
  addTelemetryEvent,
  deleteTelemetryEvents,
  getTelemetryEvents,
  getTelemetryQueueSize,
} from "./telemetryDb";
import {
  collectDeviceInfo,
  collectEnvironmentInfo,
  collectJwtInfo,
  collectNetworkInfo,
} from "./collectors";

const DEFAULT_BATCH_SIZE = 25;

const makeSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const nowIso = () => new Date().toISOString();

const safeIdle = (fn) => {
  if (typeof window !== "undefined" && window.requestIdleCallback) {
    window.requestIdleCallback(fn, { timeout: 1500 });
    return;
  }

  setTimeout(fn, 0);
};

const defaultSend = async ({ endpoint, events }) => {
  if (!endpoint || !events.length) {
    return;
  }

  const payload = { events };
  const body = JSON.stringify(payload);

  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    const accepted = navigator.sendBeacon(endpoint, blob);
    if (accepted) {
      return;
    }
  }

  await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  });
};

export const createTelemetryService = ({
  appName = TELEMETRY_DEFAULT_APP,
  appVersion = null,
  clientId = null,
  endpoint,
  getToken,
  getOfflineSyncStats,
  batchSize = DEFAULT_BATCH_SIZE,
  send = defaultSend,
} = {}) => {
  const sessionId = makeSessionId();
  let flushing = false;
  let apiRequestCount = 0;
  let apiErrorCount = 0;

  const getRuntimeSnapshot = () => {
    const token = typeof getToken === "function" ? getToken() : null;
    const offlineSync =
      typeof getOfflineSyncStats === "function" ? getOfflineSyncStats() : null;

    return {
      appName,
      appVersion,
      clientId,
      sessionId,
      timestamp: nowIso(),
      device: collectDeviceInfo(),
      network: collectNetworkInfo(),
      environment: collectEnvironmentInfo(),
      jwt: collectJwtInfo(token),
      offlineSync,
      apiErrorRate: apiRequestCount > 0 ? apiErrorCount / apiRequestCount : 0,
    };
  };

  const buildEvent = ({ eventType, payload = {} }) => {
    const token = typeof getToken === "function" ? getToken() : null;
    const offlineSync =
      typeof getOfflineSyncStats === "function" ? getOfflineSyncStats() : null;

    return {
      eventType,
      appVersion,
      clientId,
      timestamp: nowIso(),
      payload: {
        ...payload,
        appName,
        appVersion,
        device: collectDeviceInfo(),
        network: collectNetworkInfo(),
        environment: collectEnvironmentInfo(),
        jwt: collectJwtInfo(token),
        offlineSync,
      },
      sessionId,
      createdAt: Date.now(),
    };
  };

  const track = (eventType, payload = {}) => {
    const event = buildEvent({ eventType, payload });
    safeIdle(() => {
      addTelemetryEvent(event).catch(() => {
        // Telemetry must never interrupt user interactions.
      });
    });

    return event;
  };

  const flush = async () => {
    if (flushing || (typeof navigator !== "undefined" && !navigator.onLine)) {
      return;
    }

    flushing = true;
    try {
      const events = await getTelemetryEvents(batchSize);
      if (!events.length) {
        return;
      }

      await send({ endpoint, events });
      await deleteTelemetryEvents(events.map((event) => event.id));
    } catch (_error) {
      // Keep events queued for future retries.
    } finally {
      flushing = false;
    }
  };

  const trackError = (error, metadata = {}) => {
    const message = error?.message || "Unknown runtime error";
    return track(TELEMETRY_EVENT_TYPES.RUNTIME_ERROR, {
      message,
      name: error?.name || "Error",
      stack: error?.stack || null,
      metadata,
    });
  };

  const trackApiError = (error, metadata = {}) => {
    apiErrorCount += 1;
    return track(TELEMETRY_EVENT_TYPES.API_ERROR, {
      status: error?.response?.status || null,
      method: error?.config?.method || null,
      url: error?.config?.url || null,
      message: error?.message || "API error",
      responseData: error?.response?.data || null,
      metadata,
    });
  };

  const trackBusinessEvent = (name, payload = {}) => {
    return track(TELEMETRY_EVENT_TYPES.BUSINESS_EVENT, {
      name,
      ...payload,
    });
  };

  const trackApiRequest = (metadata = {}) => {
    apiRequestCount += 1;
    return track(TELEMETRY_EVENT_TYPES.API_REQUEST, metadata);
  };

  const trackSwError = (error, metadata = {}) => {
    return track(TELEMETRY_EVENT_TYPES.SW_ERROR, {
      message: error?.message || String(error) || "Service Worker error",
      name: error?.name || "Error",
      stack: error?.stack || null,
      metadata,
    });
  };

  const trackIdbError = (error, operation, metadata = {}) => {
    return track(TELEMETRY_EVENT_TYPES.IDB_ERROR, {
      message: error?.message || String(error) || "IndexedDB error",
      name: error?.name || "Error",
      operation: operation || null,
      metadata,
    });
  };

  const trackSyncFailure = (reason, metadata = {}) => {
    return track(TELEMETRY_EVENT_TYPES.SYNC_FAILURE, {
      reason: reason || "unknown",
      metadata,
    });
  };

  const trackPerformanceMetric = (name, valueMs, metadata = {}) => {
    return track(TELEMETRY_EVENT_TYPES.PERFORMANCE_METRIC, {
      name,
      valueMs,
      metadata,
    });
  };

  const initWebVitalsTracking = () => {
    // web-vitals is a peer dep — import dynamically so it never crashes if absent.
    import("web-vitals")
      .then(({ getCLS, getFID, getLCP, getTTFB, getFCP }) => {
        const report = ({ name, value, id }) => {
          trackPerformanceMetric(name, value, { metricId: id });
        };

        getCLS(report);
        getFID(report);
        getLCP(report);
        getTTFB(report);
        getFCP(report);
      })
      .catch(() => {
        // web-vitals not available — skip silently.
      });
  };

  const initGlobalErrorCapture = () => {
    if (typeof window === "undefined") {
      return () => {};
    }

    const onError = (event) => {
      trackError(
        event?.error || new Error(event?.message || "window.onerror"),
        {
          source: "window.error",
        },
      );
    };

    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (reason instanceof Error) {
        trackError(reason, { source: "unhandledrejection" });
        return;
      }

      track(TELEMETRY_EVENT_TYPES.RUNTIME_ERROR, {
        message: "Unhandled promise rejection",
        reason,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  };

  const initNetworkAutoFlush = () => {
    if (typeof window === "undefined") {
      return () => {};
    }

    const onOnline = () => {
      track(TELEMETRY_EVENT_TYPES.SYSTEM_HEALTH, {
        message: "Network reconnected, flushing telemetry queue",
      });
      flush();
    };

    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  };

  return {
    appName,
    sessionId,
    getRuntimeSnapshot,
    track,
    flush,
    trackError,
    trackSwError,
    trackIdbError,
    trackSyncFailure,
    trackPerformanceMetric,
    trackApiRequest,
    initWebVitalsTracking,
    trackApiError,
    trackBusinessEvent,
    initGlobalErrorCapture,
    initNetworkAutoFlush,
    getQueueSize: getTelemetryQueueSize,
  };
};
